import { Input, Typography } from "antd";
import {
    useGetIntegrationCustomFieldsQuery,
    useStoreIntegrationCustomFieldMutation,
    useUpdateIntegrationCustomFieldMutation,
} from "api/TenantSlug/IntegrationCustomField/hooks";
import { AxiosError } from "axios";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { createPredefinedKeysCategory } from "components/Forms/AddApiToolCustomFieldForm/predefinedKeys.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { QueryState } from "components/UI/QueryState/QueryState";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect, useMemo } from "react";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { ApiErrorResponse, FormId } from "types/models.ts";

import { AddApiToolCustomFieldData, createSchema } from "./formSchema.ts";

import IntegrationCustomFieldData = App.Data.IntegrationCustomFieldData;

interface AddApiToolCustomFieldsContentProps {
    integrationId: string;
    customFieldData?: IntegrationCustomFieldData;
    usedKeys: string[];
    onSubmit?: () => void;
}

interface AddApiToolCustomFieldsProps {
    integrationId: string;
    customFieldId?: string;
    onSubmit?: () => void;
}

const NEW_CUSTOM_FIELD = {
    parameter: "",
    value: "",
    description: "",
};

/** tag-only TagTextInput reports "{key}" — the BE `parameter` field stores the bare key. */
const stripBraces = (value: string): string =>
    value.startsWith("{") && value.endsWith("}") ? value.slice(1, -1) : value;

const AddApiToolCustomFieldsContent: FC<AddApiToolCustomFieldsContentProps> = ({ customFieldData, usedKeys }) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tCC } = usePaldockTranslation("components.connection-creator");
    const generalFormUtils = useGeneralForm<IntegrationCustomFieldData>();
    const { control } = generalFormUtils;
    const keysCategory = useMemo(() => [createPredefinedKeysCategory(tCC, usedKeys)], [tCC, usedKeys]);

    useEffect(() => {
        if (customFieldData) {
            generalFormUtils.reset(customFieldData);
        } else {
            generalFormUtils.reset(NEW_CUSTOM_FIELD);
        }
    }, [customFieldData]);

    const list = PropertiesList.create(customFieldData ?? NEW_CUSTOM_FIELD, [
        {
            term: tFields("parameter.label"),
            description: ({ parameter }) => parameter,
            render: (
                <Controller
                    control={control}
                    name="parameter"
                    render={({ field, fieldState }) => (
                        <div>
                            <TagTextInput
                                value={field.value ? `{${field.value}}` : ""}
                                onChange={(value) => field.onChange(stripBraces(value))}
                                categories={keysCategory}
                                mode="tag-only"
                                maxTags={1}
                                placeholder={tFields("parameter.placeholder")}
                            />
                            {fieldState.error?.message && (
                                <Typography.Text type="danger" className="block mt-1">
                                    {fieldState.error.message}
                                </Typography.Text>
                            )}
                        </div>
                    )}
                />
            ),
        },
        {
            term: tFields("value.label"),
            description: ({ value }) => value,
            render: (
                <Controller
                    control={control}
                    name="value"
                    render={({ field, fieldState }) => (
                        <div>
                            <Input {...field} value={field.value ?? ""} placeholder={tFields("value.placeholder")} />
                            {fieldState.error?.message && (
                                <Typography.Text type="danger" className="block mt-1">
                                    {fieldState.error.message}
                                </Typography.Text>
                            )}
                        </div>
                    )}
                />
            ),
        },
        {
            term: tFields("description.label"),
            description: ({ description }) => description,
            render: (
                <Controller
                    control={control}
                    name="description"
                    render={({ field, fieldState }) => (
                        <div>
                            <Input
                                {...field}
                                value={field.value ?? ""}
                                placeholder={tFields("description.placeholder")}
                            />
                            {fieldState.error?.message && (
                                <Typography.Text type="danger" className="block mt-1">
                                    {fieldState.error.message}
                                </Typography.Text>
                            )}
                        </div>
                    )}
                />
            ),
        },
    ]);

    return <PropertiesList list={list} />;
};

export const AddApiToolCustomFieldForm: FC<AddApiToolCustomFieldsProps> = ({
    integrationId,
    customFieldId,
    onSubmit,
}) => {
    const { t } = usePaldockTranslation("forms.add-api-tool-custom-field");
    const { handleErrorsFromErrorResponse } = useGeneralForm<AddApiToolCustomFieldData>();

    // Always fetched (not just when editing) — needed to validate/exclude already-used keys on create too.
    const { isLoading, isError, data } = useGetIntegrationCustomFieldsQuery(integrationId);

    const field = data?.integrationCustomFields.find((field) => field.id === customFieldId);
    const usedKeys = (data?.integrationCustomFields ?? [])
        .filter((customField) => customField.id !== field?.id)
        .map((customField) => customField.parameter);

    const { mutate: createCustomField } = useStoreIntegrationCustomFieldMutation(integrationId);
    const { mutate: updateCustomField } = useUpdateIntegrationCustomFieldMutation(integrationId, field?.id ?? "", {
        enabled: !!field?.id,
    });

    const onSuccess = () => {
        toast.success(t(!field?.id ? "messages.add_success" : "messages.update_success"));
        onSubmit?.();
    };

    const onError = (error: AxiosError<ApiErrorResponse>) => {
        toast.error(t(!field?.id ? "messages.add_error" : "messages.update_error"));
        handleErrorsFromErrorResponse(error);
    };

    const onSubmitForm = (values: AddApiToolCustomFieldData) => {
        if (!field?.id) {
            createCustomField(values, { onSuccess, onError });
        } else {
            updateCustomField(values, { onSuccess, onError });
        }
    };

    const defaultValues: AddApiToolCustomFieldData = {
        parameter: "",
        value: "",
        description: "",
    };

    if (isLoading || isError) {
        return <QueryState isLoading={isLoading} isError={isError} />;
    }

    return (
        <GeneralFormProvider<AddApiToolCustomFieldData>
            key={field?.id || "create"}
            id={FormId.AddApiToolCustomField}
            schema={createSchema(usedKeys)}
            defaultValues={field || defaultValues}
            onSubmit={onSubmitForm}
            resetOnSubmit={false}
        >
            <AddApiToolCustomFieldsContent
                integrationId={integrationId}
                customFieldData={field}
                usedKeys={usedKeys}
                onSubmit={onSubmit}
            />
        </GeneralFormProvider>
    );
};
